import { useState, type FC } from 'react';
import {
  canDownload,
  executeDownload,
  FILE_NOT_LINKED_TITLE,
  openInNewTab,
} from '@/lib/download';
import type {
  EditorObject,
  EditorObjectFile,
  EditorObjectVersion,
  EditorTabKey,
} from '../types';
import {
  ArrowLeftIcon,
  CloseIcon,
  DownloadIcon,
  FileTextIcon,
  PlusIcon,
  UserIcon,
} from './EditorIcons';

interface EditorObjectDetailOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  object: EditorObject | null;
  onUpdateStatus: (id: string, status: string, nextStep?: string, actionNote?: string) => Promise<void>;
  onAddVersion: (objectId: string, versionNumber: string, title: string, changelog: string) => Promise<void>;
  onAddParticipant: (objectId: string, name: string, role: string, userCode: string) => Promise<void>;
  onAddApproval: (objectId: string, stageName: string, decision: string, comment: string) => Promise<void>;
  onOpenConnections: () => void;
  onOpenFiles: () => void;
  sourceTab?: EditorTabKey;
}

export const EditorObjectDetailOverlay: FC<EditorObjectDetailOverlayProps> = ({
  isOpen,
  onClose,
  onBack,
  object,
  onUpdateStatus,
  onAddVersion,
  onAddParticipant,
  onAddApproval,
  onOpenConnections,
  onOpenFiles,
}) => {
  const versionDownloadFields = (ver: EditorObjectVersion) => ({ file_path: ver.file_path });
  const objectFileDownloadFields = (file: EditorObjectFile) => ({ file_path: file.file_path });

  const [activeSection, setActiveSection] = useState<'MATERIALS' | 'PARTICIPANTS' | 'APPROVALS' | 'HISTORY' | 'FILES'>('MATERIALS');
  const [showVersionForm, setShowVersionForm] = useState(false);
  const [newVerNumber, setNewVerNumber] = useState('');
  const [newVerTitle, setNewVerTitle] = useState('');
  const [newVerChangelog, setNewVerChangelog] = useState('');

  const [showParticipantForm, setShowParticipantForm] = useState(false);
  const [newPartName, setNewPartName] = useState('');
  const [newPartRole, setNewPartRole] = useState('REVIEWER');
  const [newPartCode, setNewPartCode] = useState('');

  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [stageName, setStageName] = useState('Финальная сверка верстки');
  const [approvalDecision, setApprovalDecision] = useState('APPROVED');
  const [approvalComment, setApprovalComment] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !object) return null;

  const handleCreateVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVerNumber.trim() || !newVerTitle.trim()) return;
    try {
      setIsSubmitting(true);
      await onAddVersion(object.id, newVerNumber, newVerTitle, newVerChangelog);
      setNewVerNumber('');
      setNewVerTitle('');
      setNewVerChangelog('');
      setShowVersionForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPartName.trim()) return;
    try {
      setIsSubmitting(true);
      await onAddParticipant(object.id, newPartName, newPartRole, newPartCode);
      setNewPartName('');
      setNewPartCode('');
      setShowParticipantForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateApproval = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await onAddApproval(object.id, stageName, approvalDecision, approvalComment);
      setApprovalComment('');
      setShowApprovalForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickStatus = async (status: string, nextStep: string, note: string) => {
    try {
      setIsSubmitting(true);
      await onUpdateStatus(object.id, status, nextStep, note);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>{object.object_code} · Редактор проекта</small>
              <h2>
                {object.kind === 'ARTICLE'
                  ? 'Статья'
                  : object.kind === 'COAUTHOR'
                    ? 'Соавторство'
                    : object.kind === 'EDITORIAL'
                      ? 'Редакционный проект'
                      : object.kind === 'MATERIAL'
                        ? 'Материалы'
                        : object.kind === 'APPROVAL'
                          ? 'Согласование'
                          : 'Завершённые'}
              </h2>
              <p>{object.title}</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button type="button" className="cab-btn" onClick={onBack}>
              <ArrowLeftIcon className="w-4 h-4" />
              Назад
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-body">
          <section className="cabinet-hero" style={{ ['--accent' as string]: '#41d2ff' }}>
            <div>
              <div className="cabinet-eyebrow">Редакционный проект · рабочий центр</div>
              <h2>{object.title}</h2>
              <p>
                {object.status}. {object.next_step || 'Сверить замечания и финальную версию'}
              </p>
            </div>
            <span className="cabinet-badge" style={{ ['--badge' as string]: '#e2ad3a' }}>
              Роль: Редактор проекта
            </span>
          </section>

          <article className="cabinet-card" style={{ ['--accent' as string]: '#8faebb', marginTop: 14 }}>
            <h4 style={{ margin: '0 0 6px' }}>Рабочая карточка</h4>
            <p style={{ margin: '0 0 12px', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
              Внутри сохраняются материалы, участники, согласования, история решений и связанные
              файлы.
            </p>
            <div className="cabinet-grid2" style={{ gap: 8 }}>
              <div className="cabinet-note">
                <span style={{ fontSize: 10, color: '#8faebb' }}>Автор</span>
                <strong style={{ display: 'block', marginTop: 4 }}>
                  {object.author_name || 'Не указан'}
                </strong>
              </div>
              <div className="cabinet-note">
                <span style={{ fontSize: 10, color: '#8faebb' }}>Направление</span>
                <strong style={{ display: 'block', marginTop: 4 }}>{object.kind}</strong>
              </div>
              <div className="cabinet-note">
                <span style={{ fontSize: 10, color: '#8faebb' }}>Статус</span>
                <strong style={{ display: 'block', marginTop: 4, color: '#41d2ff' }}>
                  {object.status}
                </strong>
              </div>
              <div className="cabinet-note">
                <span style={{ fontSize: 10, color: '#8faebb' }}>Дата</span>
                <strong style={{ display: 'block', marginTop: 4 }}>
                  {object.target_deadline || '27.08.2026'}
                </strong>
              </div>
            </div>
            <div className="cabinet-actions" style={{ marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" className="cabinet-btn" onClick={onBack}>
                ← К моим заявкам
              </button>
              <button type="button" className="cabinet-btn" onClick={onOpenConnections}>
                Академические связи
              </button>
              <button type="button" className="cabinet-btn" onClick={onOpenFiles}>
                Файлы и документы
              </button>
            </div>
          </article>

          <div className="cabinet-filters" style={{ marginTop: 14, marginBottom: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`cabinet-filter${activeSection === 'MATERIALS' ? ' active' : ''}`}
              onClick={() => setActiveSection('MATERIALS')}
            >
              Материалы и версии ({object.versions?.length || 0})
            </button>
            <button
              type="button"
              className={`cabinet-filter${activeSection === 'PARTICIPANTS' ? ' active' : ''}`}
              onClick={() => setActiveSection('PARTICIPANTS')}
            >
              Участники и команда ({object.participants?.length || 0})
            </button>
            <button
              type="button"
              className={`cabinet-filter${activeSection === 'APPROVALS' ? ' active' : ''}`}
              onClick={() => setActiveSection('APPROVALS')}
            >
              Согласования и решения ({object.approvals?.length || 0})
            </button>
            <button
              type="button"
              className={`cabinet-filter${activeSection === 'HISTORY' ? ' active' : ''}`}
              onClick={() => setActiveSection('HISTORY')}
            >
              История решений ({object.decisions?.length || 0})
            </button>
            <button
              type="button"
              className={`cabinet-filter${activeSection === 'FILES' ? ' active' : ''}`}
              onClick={() => setActiveSection('FILES')}
            >
              Связанные файлы ({object.files?.length || 0})
            </button>
          </div>

        <div className="flex-1 pb-12">
          {activeSection === 'MATERIALS' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-200">
                  Реестр версий рукописи
                </h3>
                <button
                  type="button"
                  onClick={() => setShowVersionForm(!showVersionForm)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-xs font-semibold transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Добавить версию</span>
                </button>
              </div>

              {showVersionForm && (
                <form
                  onSubmit={handleCreateVersion}
                  className="bg-slate-900/90 border border-cyan-500/30 rounded-2xl p-5 space-y-4"
                >
                  <h4 className="text-xs font-bold text-cyan-300 uppercase">
                    Загрузка новой итерации рукописи
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Номер версии (например v2.5)
                      </label>
                      <input
                        type="text"
                        required
                        value={newVerNumber}
                        onChange={(e) => setNewVerNumber(e.target.value)}
                        placeholder="v2.5"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Заголовок версии
                      </label>
                      <input
                        type="text"
                        required
                        value={newVerTitle}
                        onChange={(e) => setNewVerTitle(e.target.value)}
                        placeholder="Финальные правки библиографии"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Примечания и changelog
                    </label>
                    <textarea
                      value={newVerChangelog}
                      onChange={(e) => setNewVerChangelog(e.target.value)}
                      rows={2}
                      placeholder="Опишите внесенные изменения..."
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowVersionForm(false)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 text-xs text-slate-400"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-xs font-semibold text-white transition-colors"
                    >
                      Сохранить версию
                    </button>
                  </div>
                </form>
              )}

              <div className="space-y-3">
                {object.versions?.map((ver) => (
                  <div
                    key={ver.id}
                    className={`p-4 rounded-xl border transition-colors ${
                      ver.is_current
                        ? 'bg-slate-900/90 border-cyan-500/50 shadow-md shadow-cyan-500/10'
                        : 'bg-slate-900/50 border-slate-800'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="font-mono font-bold text-xs text-cyan-400">
                            {ver.version_number}
                          </span>
                          {ver.is_current && (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-semibold border border-cyan-500/30">
                              Текущая активная
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            · {ver.uploaded_by || 'Автор'}
                          </span>
                        </div>
                        <h4 className="text-sm font-semibold text-white">
                          {ver.title}
                        </h4>
                        {ver.changelog && (
                          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                            {ver.changelog}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          disabled={!canDownload(versionDownloadFields(ver))}
                          title={
                            canDownload(versionDownloadFields(ver))
                              ? 'Скачать'
                              : FILE_NOT_LINKED_TITLE
                          }
                          aria-label={
                            canDownload(versionDownloadFields(ver))
                              ? 'Скачать'
                              : FILE_NOT_LINKED_TITLE
                          }
                          onClick={() =>
                            void executeDownload(
                              versionDownloadFields(ver),
                              ver.file_name || ver.title,
                            )
                          }
                          className="flex items-center space-x-1 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-xs text-slate-200 transition-colors"
                        >
                          <DownloadIcon className="w-3.5 h-3.5" />
                          <span>Скачать</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'PARTICIPANTS' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-200">
                  Состав участников и экспертов
                </h3>
                <button
                  type="button"
                  onClick={() => setShowParticipantForm(!showParticipantForm)}
                  className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-xs font-semibold transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Назначить участника</span>
                </button>
              </div>

              {showParticipantForm && (
                <form
                  onSubmit={handleCreateParticipant}
                  className="bg-slate-900/90 border border-cyan-500/30 rounded-2xl p-5 space-y-4"
                >
                  <h4 className="text-xs font-bold text-cyan-300 uppercase">
                    Назначение участника / рецензента
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        ФИО участника
                      </label>
                      <input
                        type="text"
                        required
                        value={newPartName}
                        onChange={(e) => setNewPartName(e.target.value)}
                        placeholder="Prof. Aliyev"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Роль в объекте
                      </label>
                      <select
                        value={newPartRole}
                        onChange={(e) => setNewPartRole(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      >
                        <option value="AUTHOR">Автор / соавтор</option>
                        <option value="REVIEWER">Рецензент</option>
                        <option value="EDITOR">Редактор проекта</option>
                        <option value="ASSISTANT">Ассистент</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        ZA-код (если есть)
                      </label>
                      <input
                        type="text"
                        value={newPartCode}
                        onChange={(e) => setNewPartCode(e.target.value)}
                        placeholder="ZA000015"
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowParticipantForm(false)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 text-xs text-slate-400"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-xs font-semibold text-white transition-colors"
                    >
                      Назначить
                    </button>
                  </div>
                </form>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {object.participants?.map((part) => (
                  <div
                    key={part.id}
                    className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 flex items-center space-x-3"
                  >
                    <div className="p-2.5 rounded-xl bg-slate-800 border border-slate-700 text-cyan-400">
                      <UserIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-white">
                        {part.name}
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        {part.role} {part.user_code ? `· ${part.user_code}` : ''}
                      </p>
                      <span className="text-[10px] text-emerald-400 mt-0.5 block">
                        Дедлайн: {part.deadline || 'Не установлен'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'APPROVALS' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-200">
                  Этапы согласования и экспертные решения
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() =>
                      handleQuickStatus(
                        'Согласовано',
                        'Передать в выпуск',
                        'Материал полностью принят и согласован'
                      )
                    }
                    className="px-3 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs font-semibold transition-colors"
                  >
                    Принять материал
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      handleQuickStatus(
                        'Возвращено на доработку',
                        'Устранить замечания рецензента',
                        'Рукопись возвращена автору на доработку'
                      )
                    }
                    className="px-3 py-1.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 text-xs font-semibold transition-colors"
                  >
                    Вернуть на доработку
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowApprovalForm(!showApprovalForm)}
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-xs font-semibold transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Добавить решение</span>
                  </button>
                </div>
              </div>

              {showApprovalForm && (
                <form
                  onSubmit={handleCreateApproval}
                  className="bg-slate-900/90 border border-cyan-500/30 rounded-2xl p-5 space-y-4"
                >
                  <h4 className="text-xs font-bold text-cyan-300 uppercase">
                    Фиксация этапа согласования
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Этап
                      </label>
                      <input
                        type="text"
                        required
                        value={stageName}
                        onChange={(e) => setStageName(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Решение
                      </label>
                      <select
                        value={approvalDecision}
                        onChange={(e) => setApprovalDecision(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      >
                        <option value="APPROVED">Согласовано (Approved)</option>
                        <option value="RETURNED">Возвращено (Returned)</option>
                        <option value="PENDING">Ожидает (Pending)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Комментарий эксперта
                    </label>
                    <textarea
                      value={approvalComment}
                      onChange={(e) => setApprovalComment(e.target.value)}
                      rows={2}
                      placeholder="Опишите основание принятого решения..."
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowApprovalForm(false)}
                      className="px-3 py-1.5 rounded-lg bg-slate-800 text-xs text-slate-400"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-xs font-semibold text-white transition-colors"
                    >
                      Зафиксировать
                    </button>
                  </div>
                </form>
              )}

              <div className="space-y-3">
                {object.approvals?.map((app) => (
                  <div
                    key={app.id}
                    className="p-4 rounded-xl bg-slate-900/60 border border-slate-800"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div>
                        <span className="text-xs font-bold text-white">
                          {app.stage_name}
                        </span>
                        <span className="text-xs text-slate-400 ml-2">
                          · {app.actor_name} ({app.actor_role})
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                          app.decision === 'APPROVED'
                            ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                            : app.decision === 'RETURNED'
                            ? 'bg-rose-500/10 text-rose-300 border-rose-500/30'
                            : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                        }`}
                      >
                        {app.decision}
                      </span>
                    </div>
                    {app.comment && (
                      <p className="text-xs text-slate-300 bg-slate-950/40 p-2.5 rounded-lg border border-slate-800/80">
                        {app.comment}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'HISTORY' && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-200">
                Неизменяемый реестр решений и переходов
              </h3>
              <div className="space-y-3 relative pl-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-800">
                {object.decisions?.map((dec) => (
                  <div
                    key={dec.id}
                    className="relative bg-slate-900/60 border border-slate-800 rounded-xl p-3.5 text-xs"
                  >
                    <div className="absolute -left-[21px] top-4 w-2.5 h-2.5 rounded-full bg-cyan-400 ring-4 ring-black" />
                    <div className="flex items-center justify-between text-slate-400 mb-1">
                      <span className="font-bold text-slate-200">
                        {dec.action}
                      </span>
                      <span className="text-[10px] font-mono">
                        {dec.created_at ? new Date(dec.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-slate-300 mb-1">
                      {dec.notes}
                    </p>
                    <div className="text-[10px] text-slate-500">
                      Исполнитель: {dec.actor_name}
                      {dec.previous_status ? ` | Смена: ${dec.previous_status} → ${dec.new_status}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'FILES' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-200">
                  Документы, связанные с объектом
                </h3>
                <button
                  type="button"
                  onClick={onOpenFiles}
                  className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 text-xs font-semibold transition-colors"
                >
                  <FileTextIcon className="w-4 h-4" />
                  <span>Открыть общий архив</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {object.files?.map((f) => (
                  <div
                    key={f.id}
                    className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-slate-800 text-cyan-400 font-mono text-xs font-bold">
                        {f.file_type}
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-white">
                          {f.name}
                        </h4>
                        <span className="text-[10px] text-slate-400">
                          {f.version} · {Math.round(f.file_size_bytes / 1024)} KB
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={!canDownload(objectFileDownloadFields(f))}
                      onClick={() => openInNewTab(f.file_path)}
                      className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition-colors"
                      title={
                        canDownload(objectFileDownloadFields(f))
                          ? 'Просмотреть'
                          : FILE_NOT_LINKED_TITLE
                      }
                      aria-label={
                        canDownload(objectFileDownloadFields(f))
                          ? 'Просмотреть'
                          : FILE_NOT_LINKED_TITLE
                      }
                    >
                      <DownloadIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};
